import { useCallback, useEffect, useRef, useState } from 'react';
import type { TierId } from '#shared/core/ratings';
import type { RatingCard } from '#shared/schemas/rating';
import { cardOvr } from './model';

/**
 * The pack-reveal state machine (ux.md "Pack reveal"), pure part. The states are frozen in
 * PlayerCard.tsx (REVEAL_STATES); this file says what each one shows and how long it lasts.
 *
 *   sealed       the card back. Waits for a tap.
 *   tear         the top strip flies off.
 *   silhouette   the card flips to the front: the figure is a solid ink shape and the name shows.
 *   flash        the figure gets its colors, the tier tag and the OVR shape stamp in.
 *   count        the OVR counts up from 0.
 *   attributes   the six bars fill (the medium card shows its three).
 *   badges       ribbon, state tag and jersey chip pop in.
 *   settled      the resting card. Tap anywhere skips straight here.
 *
 * Reduced motion goes straight to settled. Draft Night plays the sequence in lowest-OVR-first
 * order (`draftNightOrder`), so the best card comes last.
 */

export const REVEAL_ORDER = [
  'sealed',
  'tear',
  'silhouette',
  'flash',
  'count',
  'attributes',
  'badges',
  'settled',
] as const;
export type RevealStateName = (typeof REVEAL_ORDER)[number];

/** How long each state stays before the next one starts (ms). `sealed` and `settled` wait. */
export const REVEAL_STEP_MS: Record<RevealStateName, number> = {
  sealed: 0,
  tear: 380,
  silhouette: 1100,
  flash: 520,
  count: 1300,
  attributes: 800,
  badges: 520,
  settled: 0,
};

/** Length of the OVR count-up inside the `count` state. The step adds a short hold after it. */
export const COUNT_MS = 1200;
/** Stagger between attribute bars (ms), ux.md "70ms for attribute bars". */
export const BAR_STAGGER_MS = 70;

export function nextRevealState(s: RevealStateName): RevealStateName {
  const i = REVEAL_ORDER.indexOf(s);
  return REVEAL_ORDER[Math.min(REVEAL_ORDER.length - 1, i + 1)] ?? 'settled';
}

/** States still to play after `from`, ending in settled. Reduced motion skips them all. */
export function revealPath(from: RevealStateName, reduced: boolean): RevealStateName[] {
  if (reduced) return ['settled'];
  const out: RevealStateName[] = [];
  for (let s = from; s !== 'settled'; ) {
    s = nextRevealState(s);
    out.push(s);
  }
  return out;
}

export interface RevealFlags {
  /** The front of the card is visible (false while sealed and during the tear). */
  face: boolean;
  figure: 'hidden' | 'silhouette' | 'full';
  name: boolean;
  nameSub: boolean;
  ovrShape: boolean;
  /** 'count' draws the ticking number, 'final' the real one, 'zero' the 0 before the count starts. */
  ovrNumber: 'zero' | 'count' | 'final';
  giant: boolean;
  tierTag: boolean;
  attrs: boolean;
  badges: boolean;
  /** The tier tag and the OVR shape play their stamp animation. */
  stamp: boolean;
}

const FULL: RevealFlags = {
  face: true,
  figure: 'full',
  name: true,
  nameSub: true,
  ovrShape: true,
  ovrNumber: 'final',
  giant: true,
  tierTag: true,
  attrs: true,
  badges: true,
  stamp: false,
};

/** What each state draws. `undefined` (no reveal) is the full card. */
export function revealFlags(state: RevealStateName | undefined): RevealFlags {
  if (!state || state === 'settled') return FULL;
  const at = REVEAL_ORDER.indexOf(state);
  const reached = (s: RevealStateName) => at >= REVEAL_ORDER.indexOf(s);
  return {
    face: reached('silhouette'),
    figure: !reached('silhouette') ? 'hidden' : reached('flash') ? 'full' : 'silhouette',
    name: reached('silhouette'),
    nameSub: reached('badges'),
    ovrShape: reached('flash'),
    ovrNumber: reached('count') ? (state === 'count' ? 'count' : 'final') : 'zero',
    giant: reached('count'),
    tierTag: reached('flash'),
    attrs: reached('attributes'),
    badges: reached('badges'),
    stamp: state === 'flash',
  };
}

/** What a tier adds to the flash: confetti from All-Court up, a bigger burst for Myth. */
export function tierEffects(tier: TierId | null): { confetti: number; haptic: 'tap' | 'stamp' } {
  switch (tier) {
    case 'allcourt':
      return { confetti: 40, haptic: 'stamp' };
    case 'franchise':
      return { confetti: 70, haptic: 'stamp' };
    case 'myth':
      return { confetti: 120, haptic: 'stamp' };
    default:
      return { confetti: 0, haptic: 'tap' };
  }
}

/**
 * Draft Night order: lowest OVR first, so the best card is the last one opened. Unrated cards
 * come first. Ties go by name so the order is the same on every phone. Does not mutate.
 */
export function draftNightOrder<T extends { player: { displayName: string }; rating: RatingCard | null }>(
  items: readonly T[],
): T[] {
  return [...items].sort((a, b) => {
    const oa = cardOvr(a.rating) ?? -1;
    const ob = cardOvr(b.rating) ?? -1;
    return oa - ob || a.player.displayName.localeCompare(b.player.displayName);
  });
}

export interface RevealSequence {
  state: RevealStateName;
  /** Rip the pack: sealed to tear and on. */
  begin(): void;
  /** Jump to settled. */
  skip(): void;
  /** Back to sealed (the next card in Draft Night). */
  reset(): void;
}

/**
 * Walks the states with timers. `reduced` starts and stays on settled. `autoStart` rips the pack
 * on mount (Draft Night does that after its own countdown).
 *
 * A run is one card shown from the moment it mounts or is reset until it settles. `onSettled`
 * fires exactly once per run, with or without motion: after the show or a skip, and under reduced
 * motion right on mount and again after every `reset()` (the card is settled the whole time, so
 * the run is over as soon as it starts). It says "this card is on screen and done", so use it to
 * show a Next button, not to move on by itself.
 */
export function useRevealSequence({
  reduced,
  autoStart = false,
  onSettled,
  onState,
}: {
  reduced: boolean;
  autoStart?: boolean;
  onSettled?: () => void;
  onState?: (s: RevealStateName) => void;
}): RevealSequence {
  const [state, setState] = useState<RevealStateName>(reduced ? 'settled' : 'sealed');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settledCb = useRef(onSettled);
  const stateCb = useRef(onState);
  settledCb.current = onSettled;
  stateCb.current = onState;
  // The run counter: reset() opens a new run, and each run fires onSettled once. Without it a
  // reset under reduced motion (settled to settled, no state change) would never fire again.
  const [run, setRun] = useState(0);
  const firedRun = useRef(-1);

  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const go = useCallback((s: RevealStateName) => {
    setState(s);
    stateCb.current?.(s);
  }, []);

  const begin = useCallback(() => {
    clear();
    if (reduced) return go('settled');
    go('tear');
  }, [reduced, go, clear]);

  const skip = useCallback(() => {
    clear();
    go('settled');
  }, [go, clear]);

  const reset = useCallback(() => {
    clear();
    setRun((r) => r + 1);
    go(reduced ? 'settled' : 'sealed');
  }, [reduced, go, clear]);

  // Timer chain: each state schedules the next.
  useEffect(() => {
    clear();
    if (state === 'sealed') return;
    if (state === 'settled') {
      if (firedRun.current !== run) {
        firedRun.current = run;
        settledCb.current?.();
      }
      return;
    }
    timer.current = setTimeout(() => go(nextRevealState(state)), REVEAL_STEP_MS[state]);
    return clear;
  }, [state, run, go, clear]);

  useEffect(() => {
    if (reduced) go('settled');
  }, [reduced, go]);

  // autoStart rips the pack once; a later reset or change of props must not re-rip it.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (autoStart && !reduced && !autoStarted.current) {
      autoStarted.current = true;
      begin();
    }
  }, [autoStart, reduced, begin]);

  return { state, begin, skip, reset };
}
